// Ported from Stephanet's MissedDaysSettings.jsx (Missed Days Management).
import React, { useEffect, useMemo, useState } from 'react'
import { AlarmClock, Banknote, CalendarCog, CheckCircle2, RotateCcw, Save, ShieldCheck, SlidersHorizontal, TriangleAlert, Workflow } from 'lucide-react'
import * as hr from '../api'
import Loader from '../components/Loader'
import { useHR } from '../HRContext'
import { Select } from '../components/Controls'
import { fmtMoney } from '../format'

// Missed Days Settings: every rule, threshold and switch the Missed Days module works from

const SWITCH_GROUPS = [
  ['Automation Settings', Workflow, [
    ['md_auto_generate', 'Enable Automatic Missed Day Generation', 'Auto-detect missed days from biometric and attendance data daily'],
    ['md_biometric', 'Enable Biometric Integration', 'Pull clock-in/out data from biometric devices'],
    ['md_manual_correction', 'Allow Manual Attendance Correction', 'HR can upload manual attendance to cancel missed day'],
    ['md_explanation', 'Enable Employee Explanation Workflow', 'Allow HR to send missed day to employee for explanation'],
    ['md_auto_leave', 'Enable Auto Leave Assignment', 'Automatically assign missed days to available leave entitlements'],
  ]],
  ['Approval Settings', ShieldCheck, [
    ['md_supervisor_approval', 'Supervisor Approval Required', 'Missed day must be reviewed by supervisor before HR action (the employee\'s manager, in My Approvals)'],
    ['md_hr_approval', 'HR Approval Required', 'HR manager must approve before any final action — when off, a day the supervisor authorises is paid'],
    ['md_posting_approval', 'Payroll Posting Approval Required', 'Require separate approval before posting deductions to payroll (sent as a batch)'],
    ['md_allow_override', 'Allow Override', 'HR manager can override system recommendations'],
    ['md_allow_reversal', 'Allow Reversal', 'Allow HR to reverse posted payroll deductions'],
  ]],
]
const LEAVE_SWITCHES = [
  ['md_sick_attachment', 'Require Attachment for Sick Leave', 'Employees must upload a document when sick leave is assigned'],
  ['md_medical_after_1', 'Require Medical Report After 1 Sick Day', 'Medical certificate required if sick for more than 1 day (consecutive sick days)'],
]
const LEAVES = ['Sick', 'Casual', 'Annual', 'Compassionate', 'Unpaid']
const BASES = [['gross', 'Monthly Gross Salary / Working Days'], ['basic', 'Monthly Basic Salary / Working Days'], ['net', 'Monthly Net Salary / Working Days'],
  ['fixed', 'Fixed Daily Rate'], ['grade', 'Grade-Based Daily Rate'], ['contract', 'Contract Daily Rate']]
const WDAYS = [['calendar', 'Calendar Working Days (Mon-Fri)'], ['22', 'Fixed 22 Working Days'], ['26', 'Fixed 26 Working Days'], ['custom', 'Custom Working Schedule']]
const WEEKEND = [['exclude_unless_scheduled', 'Exclude weekends unless scheduled'], ['always_exclude', 'Always exclude weekends'], ['include_if_scheduled', 'Include all days if scheduled']]
const HOLIDAY = [['exclude_unless_scheduled', 'Exclude public holidays unless scheduled'], ['always_exclude', 'Always exclude public holidays'], ['include_if_assigned', 'Include if assigned']]
// example salary for the worked calculation under the deduction settings
const EX = { gross: 300000, basic: 150000, housing: 75000, transport: 45000, daily: 12000, grade: 140000 }

function exampleRate(basis, wdays) {
  const days = wdays === '22' ? 22 : wdays === '26' ? 26 : 22
  const net = EX.gross - 0.08 * (EX.basic + EX.housing + EX.transport)
  const monthly = { gross: EX.gross, basic: EX.basic, net, grade: EX.grade }[basis]
  if (basis === 'fixed' || basis === 'contract') return { text: `Employee daily rate ${fmtMoney(EX.daily)} (monthly gross ÷ working days when none is set)`, rate: EX.daily }
  const label = { gross: 'Monthly gross', basic: 'Monthly basic', net: 'Monthly net (gross − 8% pension)', grade: 'Pay grade default basic' }[basis]
  const dayText = wdays === 'calendar' ? `${days} calendar working days (varies by month)` : wdays === 'custom' ? `${days} scheduled days (from each employee's schedule)` : `${days} working days`
  return { text: `${label} ${fmtMoney(monthly)} ÷ ${dayText}`, rate: monthly / days }
}

export default function MissedDaysSettings() {
  const { notify } = useHR()
  const [saved, setSaved] = useState(null)
  const [v, setV] = useState(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const take = d => { setSaved(d); setV(JSON.parse(JSON.stringify(d))) }
  useEffect(() => { hr.mdSettings().then(take).catch(e => setError(e.message)) }, [])

  const dirty = useMemo(() => v && saved && JSON.stringify({ ...v, can: 0, customised: 0, last_auto_run: 0 }) !== JSON.stringify({ ...saved, can: 0, customised: 0, last_auto_run: 0 }), [v, saved])
  if (error && !v) return <div className="mdr"><div className="mdr-card mdr-empty">{error}</div></div>
  if (!v) return <Loader title="Loading missed days settings…" subtitle="Fetching the module rules" />

  const can = v.can.edit
  const set = (k, x) => setV(o => ({ ...o, [k]: x }))
  const flip = k => can && setV(o => ({ ...o, switches: { ...o.switches, [k]: !o.switches[k] } }))
  const setPrio = (i, t) => setV(o => { const p = [...o.priority]; const j = p.indexOf(t); if (j >= 0 && j !== i) p[j] = p[i]; p[i] = t; return { ...o, priority: p } })
  const setTh = (k, x) => setV(o => ({ ...o, thresholds: { ...o.thresholds, [k]: x } }))
  const th = v.thresholds
  const thOk = +th.warning >= 1 && +th.warning < +th.hr_query && +th.hr_query < +th.mgmt && +th.mgmt <= 31
  const ex = exampleRate(v.basis, v.working_days)

  const save = () => {
    if (!thOk) { notify('Thresholds must rise: Warning < HR Query < Management Escalation', 'bad'); return }
    setBusy(true)
    hr.mdSaveSettings({ switches: v.switches, priority: v.priority, fallback: v.fallback, basis: v.basis,
      working_days: v.working_days, grace: Number(v.grace), thresholds: { warning: +th.warning, hr_query: +th.hr_query, mgmt: +th.mgmt },
      weekend_rule: v.weekend_rule, holiday_rule: v.holiday_rule })
      .then(d => { take(d); notify(d.changes ? `Missed days settings saved (${d.changes} change${d.changes > 1 ? 's' : ''})` : 'No changes to save') })
      .catch(e => notify(e.message, 'bad')).finally(() => setBusy(false))
  }
  const saveBtn = cls => <button className={cls} disabled={!can || busy || !dirty} onClick={save}><Save size={14} /> {busy ? 'Saving…' : cls === 'mds-run' ? 'Save All Settings' : 'Save Settings'}</button>
  const Toggle = ({ k, label, hint }) => (
    <div className={`mdset-row ${can ? '' : 'ro'}`} onClick={() => flip(k)}>
      <div><b>{label}</b><small>{hint}</small></div>
      <button type="button" role="switch" aria-checked={v.switches[k]} aria-label={label} className={`mdset-switch ${v.switches[k] ? 'on' : ''}`} disabled={!can}
        onClick={e => { e.stopPropagation(); flip(k) }}><span /></button>
    </div>
  )
  const Choice = ({ label, hint, value, onChange, options }) => (
    <label className="mdset-field">
      <span><b>{label}</b>{hint && <small>{hint}</small>}</span>
      <Select value={value} disabled={!can} onChange={e => onChange(e.target.value)}>{options.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select>
    </label>
  )

  return (
    <div className="mdr mds mdset">
      <div className="mdr-top">
        <div>
          <h2>Missed Days Settings</h2>
          <p>Configure all rules, thresholds, and automation for the Missed Days module</p>
        </div>
        <div className="mdr-top-actions no-print">
          {dirty && <button className="mdr-btn" onClick={() => take(saved)}><RotateCcw size={14} /> Discard</button>}
          {saveBtn('mdp-post-all')}
        </div>
      </div>
      {!can && <div className="mdl-info"><TriangleAlert size={15} /> You can view these settings; changing them needs edit rights on Missed Days Settings.</div>}
      {dirty && <div className="mdset-dirty"><SlidersHorizontal size={14} /> You have unsaved changes.</div>}

      <div className="mdset-grid">
        {SWITCH_GROUPS.map(([title, Ico, rows]) => (
          <div key={title} className="mdr-card mdset-card">
            <div className="mds-card-title"><Ico size={16} /> {title}</div>
            {rows.map(([k, label, hint]) => <Toggle key={k} k={k} label={label} hint={hint} />)}
          </div>
        ))}

        <div className="mdr-card mdset-card">
          <div className="mds-card-title"><CalendarCog size={16} /> Leave Settings</div>
          {LEAVE_SWITCHES.map(([k, label, hint]) => <Toggle key={k} k={k} label={label} hint={hint} />)}
          {[0, 1, 2].map(i => (
            <Choice key={i} label={`Leave Assignment Priority ${i + 1}`} value={v.priority[i]} onChange={t => setPrio(i, t)}
              options={LEAVES.map(t => [t, `${t} Leave`])} />
          ))}
          <Choice label="Final Fallback" hint="When none of the priorities has enough balance" value={v.fallback} onChange={x => set('fallback', x)}
            options={[['Unpaid', 'Unpaid Leave'], ['Deduction', 'Salary Deduction']]} />
          <div className="mdset-flow">{v.priority.map((t, i) => <span key={i}>{i + 1}. {t} Leave</span>)}<span className="end">{v.fallback === 'Unpaid' ? 'Unpaid Leave' : 'Salary Deduction'}</span></div>
        </div>

        <div className="mdr-card mdset-card">
          <div className="mds-card-title"><Banknote size={16} /> Payroll Deduction Settings</div>
          <Choice label="Deduction Salary Basis" hint="Formula used to calculate daily deduction" value={v.basis} onChange={x => set('basis', x)} options={BASES} />
          <Choice label="Working Days Basis" hint="How working days in a month are counted" value={v.working_days} onChange={x => set('working_days', x)} options={WDAYS} />
          <label className="mdset-field">
            <span><b>Grace Period (Minutes)</b><small>Minutes after shift start before a clock-in counts as late</small></span>
            <input type="number" min="0" max="240" value={v.grace} disabled={!can} onChange={e => set('grace', e.target.value)} />
          </label>
          <div className="mdset-calc">
            <small>Worked example</small>
            <span>{ex.text}</span>
            <b>Daily deduction ≈ {fmtMoney(ex.rate)}</b>
          </div>
        </div>

        <div className="mdr-card mdset-card">
          <div className="mds-card-title"><AlarmClock size={16} /> Warning Thresholds</div>
          <p className="mdset-note">Set the number of missed days that trigger each warning level per calendar month.</p>
          {[['warning', 'Warning Level Threshold', "Missed days in a month to trigger 'Warning'", 'yellow'],
            ['hr_query', 'HR Query Threshold', "Missed days to trigger 'HR Query Required'", 'red'],
            ['mgmt', 'Management Escalation Threshold', "Missed days to trigger 'Management Review'", 'darkred']].map(([k, label, hint, tone]) => (
            <label key={k} className="mdset-field">
              <span><b><i className={`mdset-dot t-${tone}`} />{label}</b><small>{hint}</small></span>
              <input type="number" min="1" max="31" value={th[k]} disabled={!can} onChange={e => setTh(k, e.target.value)} />
            </label>
          ))}
          <div className={`mdset-ladder ${thOk ? '' : 'bad'}`}>
            {thOk
              ? <><span>0–{th.warning - 1}: Normal</span><span className="w">{th.warning}–{th.hr_query - 1}: Warning</span><span className="h">{th.hr_query}–{th.mgmt - 1}: HR Query</span><span className="m">{th.mgmt}+: Management</span></>
              : 'Thresholds must rise: Warning < HR Query < Management Escalation'}
          </div>
        </div>

        <div className="mdr-card mdset-card">
          <div className="mds-card-title"><CalendarCog size={16} /> Schedule Rules</div>
          <Choice label="Weekend Rule" hint="Whether Saturdays and Sundays can be missed days" value={v.weekend_rule} onChange={x => set('weekend_rule', x)} options={WEEKEND} />
          <Choice label="Public Holiday Rule" hint="Whether public holidays can be missed days" value={v.holiday_rule} onChange={x => set('holiday_rule', x)} options={HOLIDAY} />
          <p className="mdset-note">{v.last_auto_run ? <><CheckCircle2 size={13} /> Last automatic detection: {v.last_auto_run}</> : 'Automatic detection has not run yet.'}</p>
        </div>
      </div>

      <div className="mdset-foot no-print">{saveBtn('mds-run')}</div>
    </div>
  )
}
